import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { DOCUMENT_MAX_SIZE_BYTES, documentAcceptAttribute, type DocumentCategory, type DocumentResponse } from '@timesheet/schemas';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { BottomSheet } from '../shared/bottom-sheet';
import { useNotification } from '../../contexts/NotificationContext';
import { DocumentApiError, documentsService } from '../../services/documentsService';
import { FileDropZone } from './FileDropZone';
import type { PersonOption } from './DocumentFilters';
import { categoryLabel } from './documents.utils';

interface DocumentUploadSheetProps {
  open: boolean;
  /** GENERALE, CONTRATTI or AZIENDALI (payslips have their own wizard). */
  category: Exclude<DocumentCategory, 'BUSTE_PAGA'>;
  people: PersonOption[];
  onClose: () => void;
  onUploaded: (doc: DocumentResponse) => void;
}

const stripExtension = (name: string) => name.replace(/\.[^.]+$/, '');

export const DocumentUploadSheet: React.FC<DocumentUploadSheetProps> = ({ open, category, people, onClose, onUploaded }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [targetUserId, setTargetUserId] = useState<string>('');
  const [notifyByEmail, setNotifyByEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setTitle('');
      setTitleTouched(false);
      setDescription('');
      setTargetUserId('');
      setNotifyByEmail(false);
    }
  }, [open, category]);

  const file = files[0];
  useEffect(() => {
    if (file && !titleTouched) setTitle(stripExtension(file.name));
  }, [file, titleTouched]);

  const needsPerson = category === 'CONTRATTI';
  const canSubmit = !!file && title.trim().length > 0 && (!needsPerson || targetUserId !== '');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || !file) return;
    try {
      setSubmitting(true);
      const doc = await documentsService.upload({
        file,
        category,
        title: title.trim(),
        description: description.trim() || undefined,
        targetUserId: needsPerson ? Number(targetUserId) : undefined,
        notifyByEmail: category === 'GENERALE' ? notifyByEmail : undefined,
      });
      showNotification(t('documents.upload.success', 'Documento caricato'), 'success');
      onUploaded(doc);
    } catch (error) {
      if (error instanceof DocumentApiError) {
        showNotification(t(`documents.errors.${error.code}`, t('documents.errors.generic', 'Caricamento non riuscito')), 'error');
      } else {
        console.error('Error uploading document:', error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} size="wide" title={t('documents.upload.title', 'Carica documento')}>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {t('documents.upload.categoryLabel', 'Categoria')}
          <Badge variant="secondary">{categoryLabel(category, t)}</Badge>
        </div>

        <FileDropZone
          accept={documentAcceptAttribute(category)}
          maxSizeBytes={DOCUMENT_MAX_SIZE_BYTES}
          files={files}
          onFilesChange={setFiles}
          hint={
            category === 'CONTRATTI'
              ? t('documents.upload.formatsPdf', 'Solo PDF, massimo 15 MB')
              : t('documents.upload.formatsAll', 'PDF, immagini, Word o Excel, massimo 15 MB')
          }
          disabled={submitting}
        />

        <div className="space-y-2">
          <Label htmlFor="upload-title">{t('documents.upload.titleField', 'Titolo')}</Label>
          <Input
            id="upload-title"
            value={title}
            maxLength={200}
            placeholder={t('documents.upload.titlePlaceholder', 'Es. Regolamento aziendale 2026')}
            onChange={(event) => {
              setTitleTouched(true);
              setTitle(event.target.value);
            }}
            required
          />
        </div>

        {needsPerson && (
          <div className="space-y-2">
            <Label>{t('documents.upload.person', 'Persona')}</Label>
            <Select value={targetUserId} onValueChange={setTargetUserId}>
              <SelectTrigger>
                <SelectValue placeholder={t('documents.upload.personPlaceholder', 'Scegli la persona')} />
              </SelectTrigger>
              <SelectContent>
                {people.map((person) => (
                  <SelectItem key={person.id} value={String(person.id)}>
                    {person.name}
                    {person.isActive === false ? ` (${t('documents.filters.inactive', 'cessato')})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="upload-description">{t('documents.upload.description', 'Descrizione (opzionale)')}</Label>
          <Textarea id="upload-description" value={description} maxLength={2000} rows={3} onChange={(event) => setDescription(event.target.value)} />
        </div>

        {category === 'GENERALE' && (
          <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm">
            <Checkbox checked={notifyByEmail} onCheckedChange={(value) => setNotifyByEmail(value === true)} className="mt-0.5" />
            <span>
              <span className="font-medium">{t('documents.upload.notifyByEmail', 'Invia anche una email a tutti')}</span>
              <span className="block text-xs text-muted-foreground">
                {t('documents.upload.notifyByEmailHint', 'La notifica in app viene inviata sempre; la mail solo se lo spunti.')}
              </span>
            </span>
          </label>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel', 'Annulla')}
          </Button>
          <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={!canSubmit || submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {submitting ? t('documents.upload.submitting', 'Caricamento...') : t('documents.upload.submit', 'Carica')}
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
};
